import { backupCredentials } from './credentials';
import type { SyncMeta, SyncProvider, SyncVersion } from '../sync/SyncProvider';

/**
 * The remote backup transport: `/api/backup`, and nothing more.
 *
 * It moves bytes. It does not seal them, does not open them, and does not know they are encrypted —
 * `BackupService` seals before calling `push` and `restore.ts` opens after calling `pull`. That division is
 * what lets one `SyncProvider` interface serve both a plaintext file export and a sealed upload
 * (ADR-018): the caller knows what the bytes are, the transport only knows where they go.
 *
 * ── `isConfigured()` is the whole gate ──
 *
 * It is false until a caregiver has minted the credentials, and that is what keeps every uploaded byte
 * deliberate. It is also structurally why the flow suite is safe: `vite preview` proxies `/api` to the
 * deployed staging app, so an automatic backup firing in a test would reach real infrastructure — and it
 * cannot, because a test's database has no credentials.
 */

const ENDPOINT = '/api/backup';

/** The token is a header, never a query string: a URL reaches Azure's HTTP logs, and this is a credential. */
const TOKEN_HEADER = 'x-sags-token';

export interface AzureBlobOptions {
  endpoint?: string;
  /** Injected in tests. Production reads the credentials out of `meta`. */
  token?: string | null;
}

/**
 * `SyncProvider` plus the one capability only this provider has.
 *
 * `refresh()` is not on `SyncProvider` because no other implementation needs it: the Files app has no
 * credential to pick up. Widening the shared contract for one implementation's convenience is how
 * interfaces stop meaning anything.
 */
export interface RefreshableProvider extends SyncProvider {
  /** Re-read the device token, so credentials minted after startup take effect without a relaunch. */
  refresh(): Promise<void>;
}

export function createAzureBlobProvider(options: AzureBlobOptions = {}): RefreshableProvider {
  const endpoint = options.endpoint ?? ENDPOINT;

  /**
   * Cached after the first read, because `isConfigured()` has to be synchronous — the interface says so,
   * and a caregiver's care-mode screen should not have to await a database read to decide what to show.
   * `refresh()` is called by the service before it uses the provider, so minting credentials takes effect
   * without a relaunch.
   */
  let token: string | null = options.token ?? null;

  const headers = (extra: Record<string, string> = {}): Record<string, string> => {
    if (!token) throw new Error('azureBlob: no device token');
    return { [TOKEN_HEADER]: token, ...extra };
  };

  return {
    id: 'azureBlob',

    isConfigured(): boolean {
      return Boolean(token);
    },

    async push(blob: Blob, meta: SyncMeta): Promise<void> {
      // Read to text rather than handing `fetch` the Blob directly. The envelope *is* JSON text, and a string
      // body gets a `Content-Length` where a Blob may be sent chunked — one fewer thing between an iPad on
      // hotel Wi-Fi and a backup landing. (A test noticed this first, because Playwright cannot read a
      // streamed body either; the reason to keep it is the wire, not the test.)
      const body = await blob.text();

      // Metadata travels beside the bytes because the server is forbidden to look inside them (ADR-020).
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: headers({
          'Content-Type': 'application/json',
          'x-sags-envelope': '1',
          'x-sags-schema-version': String(meta.schemaVersion),
          'x-sags-created-at': meta.createdAt,
        }),
        body,
      });

      if (!response.ok) {
        // The status is the useful part and the body may be empty; neither ever includes the payload.
        throw new Error(`azureBlob: push failed with ${response.status}`);
      }
    },

    async pull(versionId?: string): Promise<{ blob: Blob; createdAt: string } | null> {
      const url = versionId === undefined
        ? endpoint
        : `${endpoint}?version=${encodeURIComponent(versionId)}`;

      const response = await fetch(url, { headers: headers() });

      // 404 is "nothing stored", which is a fact rather than a failure — a device that has never backed up
      // and a device whose backup is unreachable must not look the same to the caller.
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`azureBlob: pull failed with ${response.status}`);

      return {
        blob: await response.blob(),
        createdAt: response.headers.get('x-sags-created-at') ?? new Date().toISOString(),
      };
    },

    async versions(): Promise<SyncVersion[]> {
      const response = await fetch(`${endpoint}/versions`, { headers: headers() });
      if (response.status === 404) return [];
      if (!response.ok) throw new Error(`azureBlob: versions failed with ${response.status}`);

      const body = (await response.json()) as {
        versions?: Array<{
          versionId?: string | null;
          createdAt?: string | null;
          lastModified?: string | null;
          sizeBytes?: number | null;
          schemaVersion?: number | null;
        }>;
      };

      return (body.versions ?? []).map((entry) => ({
        // The current version may have no id of its own; an empty string means "the latest", which is what
        // `pull()` with no argument fetches.
        id: entry.versionId ?? '',
        // `createdAt` is the client's own stamp from when the backup was made, which is what a caregiver
        // recognises. `lastModified` is when the blob was written, and only stands in if the first is absent.
        createdAt: entry.createdAt ?? entry.lastModified ?? '',
        sizeBytes: entry.sizeBytes ?? null,
        schemaVersion: entry.schemaVersion ?? null,
      }));
    },

    async refresh(): Promise<void> {
      token = (await backupCredentials())?.token ?? null;
    },
  };
}

export const azureBlobProvider = createAzureBlobProvider();
